<script>
import { transcribeAudio } from "@/shared/api/apiClientAi";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";

let recorder = null;
let source = null;
let analyser = null;

export default {
  name: "SpeechToText",
  components: { SvgIcon },
  emits: ["finish"],
  data() {
    return {
      state: null,
      result: null,
      silenceDetector: null,
      silenceThreshold: 5,
      silenceTimer: null,
      initialSilenceDuration: 10000,
      silenceDuration: 2000,
      hasSpokenDetected: false,
      elapsed: 0,
      timerInterval: null,
    };
  },
  computed: {
    stateLabel() {
      switch (this.state) {
        case "init":
          return this.$t("Получение доступа к микрофону ...");
        case "started":
          return this.$t("Запись начата");
        case "uploading":
          return this.$t("Распознавание речи ...");
        case "uploading_error":
          return this.$t("Ошибка распознавания");
        default:
          return "";
      }
    },
    formattedTime() {
      const mins = Math.floor(this.elapsed / 60);
      const secs = this.elapsed % 60;
      return `${String(mins).padStart(2, "0")}:${String(secs).padStart(
        2,
        "0"
      )}`;
    },
  },
  mounted() {
    this.startASR();
    window.addEventListener("keydown", this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
    this.clearSilenceDetection();
    this.stopTimer();
  },
  methods: {
    handleKeyDown(e) {
      if (e.key === "Enter") {
        e.preventDefault();
        if (this.state === "started") {
          this.resolve();
        }
      }
    },
    startASR() {
      this.state = "init";
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          this.state = "started";
          this.startTimer();

          recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
          recorder.start();

          const audioContext = new (window.AudioContext ||
            window.webkitAudioContext)();
          source = audioContext.createMediaStreamSource(stream);
          analyser = audioContext.createAnalyser();
          analyser.fftSize = 2048;
          source.connect(analyser);

          this.$nextTick(() => {
            this.visualize(analyser);
          });
          this.startSilenceDetection(analyser);

          recorder.ondataavailable = (e) => this.handleRecorderStop(e, stream);
        })
        .catch(() => {
          this.state = "user_media_error";
          this.finish("");
        });
    },
    reject() {
      if (this.state === "uploading") return;
      this.result = "reject";
      this.stopASR();
      this.finish("");
    },
    resolve() {
      this.result = "resolve";
      this.stopASR();
    },
    stopASR() {
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }
      if (source) source.disconnect();
      if (analyser) analyser.disconnect();
      this.clearSilenceDetection();
      this.stopTimer();
    },
    clearSilenceDetection() {
      if (this.silenceDetector) {
        cancelAnimationFrame(this.silenceDetector);
        this.silenceDetector = null;
      }
      if (this.silenceTimer) {
        clearTimeout(this.silenceTimer);
        this.silenceTimer = null;
      }
      this.hasSpokenDetected = false;
    },
    startSilenceDetection(analyserNode) {
      const bufferLength = analyserNode.fftSize;
      const dataArray = new Uint8Array(bufferLength);

      const detectSilence = () => {
        analyserNode.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += Math.abs(dataArray[i] - 128);
        }
        const average = sum / bufferLength;

        if (average >= this.silenceThreshold) {
          this.hasSpokenDetected = true;
          if (this.silenceTimer) {
            clearTimeout(this.silenceTimer);
            this.silenceTimer = null;
          }
        } else if (!this.silenceTimer) {
          const duration = this.hasSpokenDetected
            ? this.silenceDuration
            : this.initialSilenceDuration;
          this.silenceTimer = setTimeout(() => {
            if (this.state === "started") {
              this.resolve();
            }
          }, duration);
        }

        if (this.state === "started") {
          this.silenceDetector = requestAnimationFrame(detectSilence);
        }
      };

      this.silenceDetector = requestAnimationFrame(detectSilence);
    },
    handleRecorderStop(e, stream) {
      if (this.result === "resolve") {
        const blob = new Blob([e.data], { type: "audio/webm" });
        this.uploadAudio(blob);
      }
      stream.getTracks().forEach((track) => track.stop());
    },
    async uploadAudio(blob) {
      this.state = "uploading";
      try {
        const response = await transcribeAudio(blob);
        const text =
          typeof response.data === "string"
            ? response.data
            : response.data?.text || "";
        if (text) {
          this.finish(text);
        } else {
          this.handleUploadError();
        }
      } catch {
        this.handleUploadError();
      }
    },
    handleUploadError() {
      this.state = "uploading_error";
      this.finish("");
    },
    startTimer() {
      this.elapsed = 0;
      this.timerInterval = setInterval(() => {
        this.elapsed++;
      }, 1000);
    },
    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },
    visualize(analyserNode) {
      const canvas = this.$refs.visualizer;
      if (!canvas) return;
      const canvasCtx = canvas.getContext("2d");
      const bufferLength = analyserNode.fftSize;
      const dataArray = new Uint8Array(bufferLength);

      const draw = () => {
        if (this.state !== "started") return;
        requestAnimationFrame(draw);
        analyserNode.getByteTimeDomainData(dataArray);
        canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
        canvasCtx.lineWidth = 2;
        canvasCtx.strokeStyle = "hsl(var(--primary))";
        canvasCtx.beginPath();
        const sliceWidth = canvas.width / bufferLength;
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
          const v = dataArray[i] / 128.0;
          const y = (v * canvas.height) / 2;
          if (i === 0) canvasCtx.moveTo(x, y);
          else canvasCtx.lineTo(x, y);
          x += sliceWidth;
        }
        canvasCtx.lineTo(canvas.width, canvas.height / 2);
        canvasCtx.stroke();
      };
      draw();
    },
    finish(text) {
      this.$emit("finish", text);
    },
  },
};
</script>

<template>
  <div class="stt-container">
    <div v-if="state === 'started'" class="stt-recording">
      <button class="stt-btn reject" @click="reject">
        <SvgIcon name="huge-x" class="w-4 h-4" />
      </button>
      <canvas ref="visualizer" class="stt-canvas"></canvas>
      <span class="stt-timer">{{ formattedTime }}</span>
      <button class="stt-btn resolve" @click="resolve">
        <SvgIcon name="huge-check" class="w-4 h-4" />
      </button>
    </div>
    <div v-else class="stt-status">
      {{ stateLabel }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.stt-container {
  @apply rounded-2xl border border-border bg-primary/5 flex items-center justify-center p-1.5;
  height: 42px;
}

.stt-recording {
  @apply w-full flex items-center gap-2;
}

.stt-canvas {
  @apply w-full;
  height: 32px;
}

.stt-timer {
  @apply flex-shrink-0 text-xs font-medium text-primary;
}

.stt-status {
  @apply text-xs font-medium text-muted-foreground px-2;
}

.stt-btn {
  @apply flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
    cursor-pointer border-none transition-colors;

  svg {
    fill: none;
  }

  &.reject {
    @apply bg-destructive/10 text-destructive hover:bg-destructive/20;
  }

  &.resolve {
    @apply bg-primary/10 text-primary hover:bg-primary/20;
  }
}
</style>
